import { useEffect, useRef } from 'react';
import bootIntercom, { shutdown } from '@intercom/messenger-js-sdk';
import { useLocation } from 'react-router';
import useAuth from 'hooks/useAuth';
import { RootState, useSelector } from 'redux/store';
import { fetchIntercomToken } from './intercom.service';
import { buildIntercomBootArgs, isIntercomTokenUsable } from './intercom.helpers';

const APP_ID = 'tptfkcpw';

const Intercom = () => {
  const { user, isAuthenticated, isInitialized } = useAuth();
  const { pathname } = useLocation();
  const organizationProfile = useSelector(
    (state: RootState) => state.organization.organizationProfile
  );
  const tokenCacheRef = useRef<{ userId: string; token: string } | null>(null);

  useEffect(() => {
    let cancelled = false;

    const syncMessenger = async () => {
      if (!isInitialized || !APP_ID) return;

      if (!isAuthenticated || !user) {
        tokenCacheRef.current = null;
        shutdown();
        return;
      }

      const userId = String(user.userId ?? user.id);
      const cached = tokenCacheRef.current;
      let token =
        cached?.userId === userId && isIntercomTokenUsable(cached.token, Date.now())
          ? cached.token
          : null;

      if (!token) {
        try {
          token = await fetchIntercomToken();
          tokenCacheRef.current = { userId, token };
        } catch (error) {
          tokenCacheRef.current = null;
          console.error('Failed to fetch Intercom user JWT, booting anonymously', error);
        }
      }

      if (cancelled) return;

      bootIntercom(buildIntercomBootArgs(APP_ID, token, organizationProfile));
    };

    syncMessenger();

    return () => {
      cancelled = true;
    };
  }, [isInitialized, isAuthenticated, user, pathname, organizationProfile]);

  useEffect(() => {
    return () => {
      shutdown();
    };
  }, []);

  return null;
};

export default Intercom;
